import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const safeGetItem = (key) => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const safeSetItem = (key, value) => {
  try { localStorage.setItem(key, value); } catch { /* Safari private mode */ }
};
const safeRemoveItem = (key) => {
  try { localStorage.removeItem(key); } catch { /* Safari private mode */ }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(safeGetItem('token'));
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      validateToken();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchSubscription = async () => {
    try {
      const res = await api.get('/subscription/status');
      setSubscription(res.data);
    } catch { setSubscription(null); }
  };

  const validateToken = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      await fetchSubscription();
    } catch (error) {
      safeRemoveItem('token');
      setToken(null);
      delete api.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { tokens, user } = response.data;
      const access_token = tokens?.access_token;

      if (!access_token) {
        throw new Error('Token manquant dans la réponse');
      }

      safeSetItem('token', access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      setUser(user);
      fetchSubscription();

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Erreur de connexion'
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      const { tokens, user } = response.data;
      const access_token = tokens?.access_token;

      if (!access_token) {
        throw new Error('Token manquant dans la réponse');
      }

      safeSetItem('token', access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      setUser(user);
      fetchSubscription();

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Erreur d\'inscription'
      };
    }
  };

  const logout = () => {
    safeRemoveItem('token');
    setToken(null);
    setUser(null);
    setSubscription(null);
    delete api.defaults.headers.common['Authorization'];
  };

  const refreshUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch {
      // silently fail
    }
  };

  const value = {
    user,
    token,
    loading,
    subscription,
    login,
    register,
    logout,
    refreshUser,
    fetchSubscription
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
